import React, { FC, useState } from 'react';
import { Dialog, DialogContent, DialogActions, TextField, Button, Box, IconButton } from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { addInstitution } from './action.ts';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapModal from '../Warehouses/map-modal.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface AddInstitutionModalProps {
    open: boolean;
    onClose: () => void;
}

const AddInstitutionModal: FC<AddInstitutionModalProps> = ({ open, onClose }) => {
    const [institutionName, setInstitutionName] = useState('');
    const [institutionNameError, setInstitutionNameError] = useState<string>('');
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [latitudeError, setLatitudeError] = useState<string>('');
    const [longitudeError, setLongitudeError] = useState<string>('');
    const [mapModalOpen, setMapModalOpen] = useState(false);    
    const resetState = () => {
        setInstitutionName('');
        setInstitutionNameError('');
        setLatitude('');
        setLongitude('');
        setLatitudeError('');
        setLongitudeError('');
    };

    const validateLatitude = (value: string): boolean => {
        const num = Number(value);
        return value.trim() !== '' && !isNaN(num) && num >= -90 && num <= 90;
    };

    const validateLongitude = (value: string): boolean => {
        const num = Number(value);
        return value.trim() !== '' && !isNaN(num) && num >= -180 && num <= 180;
    };

    const handleInstitutionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setInstitutionName(value);

        if (value.trim() === '') {
            setInstitutionNameError('Institution Name is required');
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
            setInstitutionNameError('Invalid Character');
        } else {
            setInstitutionNameError('');
        }
    }; 

    const handleLatitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLatitude(value);
        
        if (value.trim() === '') {
            setLatitudeError('Latitude is required');
        } else if (validateLatitude(value)) {
            setLatitudeError('');
        } else {
            setLatitudeError('Invalid latitude (-90 to 90)');
        }
    };

    const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLongitude(value);
        
        if (value.trim() === '') {
            setLongitudeError('Longitude is required');
        } else if (validateLongitude(value)) {
            setLongitudeError('');
        } else {
            setLongitudeError('Invalid longitude (-180 to 180)');
        }
    };

    const handleLocationSelect = (address: string, lat: string, lng: string) => {
        setLatitude(lat);
        setLongitude(lng);
        setLatitudeError('');
        setLongitudeError('');
        setMapModalOpen(false);
    };

    const handleGoogleMapsSelect = () => {
        setMapModalOpen(true);
    };
     
const handleSave = () => {
        let hasError = false;
        
        const trimmedName = institutionName.trim();

        if (!trimmedName) {
            setInstitutionNameError('Institution Name is required');
            hasError = true;
        } else if (!/^[a-zA-Z\s]+$/.test(institutionName)) {
            setInstitutionNameError('Invalid Character');
            hasError = true;
        } else if (trimmedName.length === 1) {
            setInstitutionNameError('Invalid Name');
            hasError = true;
        }


        if (!latitude.trim()) {
            setLatitudeError('Latitude is required');
            hasError = true;
        } else if (!validateLatitude(latitude)) {
            setLatitudeError('Invalid latitude (-90 to 90)');
            hasError = true;
        }

        if (!longitude.trim()) {
            setLongitudeError('Longitude is required');
            hasError = true;
        } else if (!validateLongitude(longitude)) {
            setLongitudeError('Invalid longitude (-180 to 180)');
            hasError = true;
        }

        if (hasError || institutionNameError || latitudeError || longitudeError) {
            toast.error("Please fix the errors");
            return;
        }

        addInstitution(
            institutionName.trim(),
            latitude.trim(),
            longitude.trim(),
            (response) => {
                toast.success(response.message);
                resetState(); 
                onClose();
            },
            (errorMessage) => {
                if (errorMessage?.message) {
                    toast.error(errorMessage.message);
                }
            }
        );
    };

    const handleCancel = () => {
        resetState();
        onClose();
    };  

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            maxWidth="xs"  
            sx={{
                '& .MuiDialog-paper': {
                    width: '400px',
                    maxWidth: 'none',
                },
            }}
        >
            <Box
                sx={{
                    marginLeft: {
                        xs: '0px',
                        sm: '-180px',
                    }
                }}
            >    
                <Heading title="Add Institution" closeIcon="true" onClose={handleCancel} />
            </Box>
            <DialogContent>
                <div style={{ marginTop: '5px' }}>
                    <p style={{ fontSize: '17px' }}>Institution Name <span style={{color: 'red'}}>*</span></p>
                </div>
                <div style={{ marginLeft: '140px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='institution_name'
                            placeholder='Institution Name'
                            value={institutionName}
                            onChange={handleInstitutionNameChange}
                            error={!!institutionNameError}
                            helperText={institutionNameError}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </div>

                <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '17px' }}>Latitude <span style={{color: 'red'}}>*</span></p>
                </div>
                <div style={{ marginLeft: '100px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <IconButton
                            onClick={handleGoogleMapsSelect}
                            color="primary"
                            aria-label="select from map"

                        >
                            <LocationOnIcon />
                        </IconButton>
                        <TextField
                            size='small'
                            name='latitude'
                            placeholder='Latitude'
                            value={latitude}
                            onChange={handleLatitudeChange}
                            error={!!latitudeError}
                            helperText={latitudeError}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </div>

                <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '17px' }}>Longitude <span style={{color: 'red'}}>*</span></p>
                </div>
                <div style={{ marginLeft: '140px', marginTop: '-25px' }}>
                    <Box display="flex" alignItems="center">
                        <TextField
                            size='small'
                            name='longitude'
                            placeholder='Longitude'
                            value={longitude}
                            onChange={handleLongitudeChange}
                            error={!!longitudeError}
                            helperText={longitudeError}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </div>
            </DialogContent>
            
            <DialogActions style={{ justifyContent: 'center', marginBottom: '10px' }}>
                <Button
                    onClick={handleCancel}
                    className="custom-cancel-button"
                    style={{ marginRight: '10px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    className="custom-button"
                    style={{ marginLeft: '10px' }}
                >
                    Save
                </Button>
            </DialogActions>

            <MapModal
                open={mapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onLocationSelect={handleLocationSelect}
                initialLocation={
                    latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude))
                        ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
                        : undefined
                }
            />
        </Dialog>
    );
};

export default AddInstitutionModal;